import React from 'react';
import { TrendingUp, MessageSquare, Sparkles, Zap } from 'lucide-react';

const CHART_DAYS = 7; // Number of days to display in charts

interface StatsChartProps {
  global: {
    messagesSent: number;
    messagesGenerated: number;
    predictionsGenerated: number;
  };
  daily: {
    dates: string[];
    messagesSent: number[];
    messagesGenerated: number[];
    predictionsGenerated: number[];
  };
}

export default function StatsChart({ global, daily }: StatsChartProps) {
  // Helper function to get max value from arrays safely
  const getMaxValue = (...arrays: number[][]): number => {
    let max = 1;
    for (const arr of arrays) {
      for (const val of arr) {
        if (val > max) max = val;
      }
    }
    return max;
  };

  // Calculate max values for scaling charts
  const maxMessages = getMaxValue(daily.messagesSent, daily.messagesGenerated);
  const maxPredictions = getMaxValue(daily.predictionsGenerated);

  // Get last N days for display
  const lastNDays = {
    dates: daily.dates.slice(-CHART_DAYS),
    messagesSent: daily.messagesSent.slice(-CHART_DAYS),
    messagesGenerated: daily.messagesGenerated.slice(-CHART_DAYS),
    predictionsGenerated: daily.predictionsGenerated.slice(-CHART_DAYS),
  };

  // Format date to display (MM/DD)
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // Format large numbers with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Global Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <MessageSquare className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Messages Sent</p>
              <p className="text-2xl font-bold text-white">{formatNumber(global.messagesSent)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">AI Responses</p>
              <p className="text-2xl font-bold text-white">{formatNumber(global.messagesGenerated)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Zap className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Predictions Generated</p>
              <p className="text-2xl font-bold text-white">{formatNumber(global.predictionsGenerated)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Charts */}
      <div className="space-y-6">
        {/* Messages Chart */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Messages (Last {CHART_DAYS} Days)</h3>
          </div>
          <div className="space-y-4">
            {/* Chart */}
            <div className="flex items-end justify-between gap-2 h-48">
              {lastNDays.dates.map((date, index) => {
                const sent = lastNDays.messagesSent[index];
                const generated = lastNDays.messagesGenerated[index];
                const sentHeight = (sent / maxMessages) * 100;
                const generatedHeight = (generated / maxMessages) * 100;

                return (
                  <div key={date} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex gap-1 items-end justify-center h-full">
                      {/* User messages */}
                      <div className="relative flex-1 group">
                        <div
                          className="w-full bg-blue-500 rounded-t hover:bg-blue-400 transition-colors"
                          style={{ height: `${sentHeight}%` }}
                          title={`Sent: ${formatNumber(sent)}`}
                        />
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-xs text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          Sent: {formatNumber(sent)}
                        </div>
                      </div>
                      {/* AI messages */}
                      <div className="relative flex-1 group">
                        <div
                          className="w-full bg-purple-500 rounded-t hover:bg-purple-400 transition-colors"
                          style={{ height: `${generatedHeight}%` }}
                          title={`AI: ${formatNumber(generated)}`}
                        />
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-xs text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          AI: {formatNumber(generated)}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">{formatDate(date)}</span>
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-gray-400">User Messages</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded"></div>
                <span className="text-gray-400">AI Responses</span>
              </div>
            </div>
          </div>
        </div>

        {/* Predictions Chart */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Predictions (Last {CHART_DAYS} Days)</h3>
          </div>
          <div className="space-y-4">
            {/* Chart */}
            <div className="flex items-end justify-between gap-2 h-48">
              {lastNDays.dates.map((date, index) => {
                const predictions = lastNDays.predictionsGenerated[index];
                const height = (predictions / maxPredictions) * 100;

                return (
                  <div key={date} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex items-end justify-center h-full">
                      <div className="relative w-3/4 group">
                        <div
                          className="w-full bg-green-500 rounded-t hover:bg-green-400 transition-colors"
                          style={{ height: `${height}%` }}
                          title={`${formatNumber(predictions)} predictions`}
                        />
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-xs text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          {formatNumber(predictions)}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">{formatDate(date)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
